import React, { useState, useEffect } from 'react';
import { Select, Input } from 'antd';
import { SearchOutlined, DownCircleFilled } from '@ant-design/icons';
import './SmartSearch.css';

const { Option } = Select;

interface DemographicsSmartSearchProps {
  onSearch: (searchTerm: string, field: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  searchFields?: Array<{ value: string; label: string }>;
  initialSearchTerm?: string;
  initialSearchField?: string;
  value?: string;
  field?: string;
}

const defaultSearchableFields = [
  { value: 'all', label: 'All Fields' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'doctorName', label: 'Doctor Name' },
  { value: 'procedureName', label: 'Procedure Name' },
  { value: 'id', label: 'ID' },
];

const DemographicsSmartSearch: React.FC<DemographicsSmartSearchProps> = ({ 
  onSearch, 
  placeholder = "Search...",
  style,
  searchFields,
  initialSearchTerm = '',
  initialSearchField = 'all',
  value,
  field
}) => {
  // Use controlled values if provided, otherwise use internal state
  const [internalSearchTerm, setInternalSearchTerm] = useState(initialSearchTerm);
  const [internalSelectedField, setInternalSelectedField] = useState(initialSearchField);
  
  const searchTerm = value !== undefined ? value : internalSearchTerm;
  const selectedField = field !== undefined ? field : internalSelectedField;
  
  // Use custom search fields if provided, otherwise use defaults
  const fields = searchFields || defaultSearchableFields;
  
  // Update internal state when controlled props change
  useEffect(() => {
    if (value !== undefined) {
      setInternalSearchTerm(value);
    }
  }, [value]);
  
  useEffect(() => {
    if (field !== undefined) {
      setInternalSelectedField(field);
    }
  }, [field]);

  // Filter data as user types (real-time filtering)
  useEffect(() => {
    // Only search if there's a search term, otherwise show all data
    if (searchTerm.trim()) {
      onSearch(searchTerm.trim(), selectedField);
    } else {
      onSearch('', selectedField);
    }
  }, [searchTerm, selectedField, onSearch]);

  const handleFieldChange = (newField: string) => {
    setInternalSelectedField(newField);
  };
  
  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value || '';
    setInternalSearchTerm(newTerm);
  };

  return (
    <div className="smart-search-unified" style={style}>
      <div className="unified-search-container">
        {/* Left side - Select dropdown */}
        <div className="select-section">
          <Select
            value={selectedField}
            onChange={handleFieldChange}
            size="large"
            suffixIcon={<DownCircleFilled style={{ fontSize: '20px',color:'#00a3e0 ', pointerEvents: 'none' }} />}
            className="unified-select"
            dropdownMatchSelectWidth={false}
          >
            {fields.map(field => (
              <Option key={field.value} value={field.value}>
                {field.label}
              </Option>
            ))}
          </Select>
        </div>
        
        {/* Right side - Input field */}
        <div className="input-section">
          <Input
            placeholder="Search here..."
            value={searchTerm}
            onChange={handleSearchTermChange}
            size="small"
            allowClear
            suffix={<SearchOutlined style={{ fontSize: '16px' }} />}
            className="unified-input"
          />
        </div>
      </div>
      
      {searchTerm && (
        <div style={{ 
          marginTop: 8, 
          fontSize: '12px', 
          color: '#666',
          fontStyle: 'italic' 
        }}>
          Searching for "{searchTerm}" {selectedField !== 'all' ? `in ${fields.find(f => f.value === selectedField)?.label}` : 'across all fields'}
        </div>
      )}
    </div>
  );
};

export default DemographicsSmartSearch;

